import React, { useEffect, useRef } from 'react';
import { Calendar } from './Calendar';
import { CampaignList } from './CampaignList';
import { CampaignForm } from './CampaignForm';
import LoadingSpinner from './LoadingSpinner';
import { Campaign as BaseCampaignType } from '../types/Campaign';
import { useLocation } from 'react-router-dom';
import { logAuthEvent } from '../utils/authDebugger';
import { Profile } from '../types/Profile';
import useDocumentTitle from '../utils/useDocumentTitle';

// Define internal type for strict Date objects
interface InternalCampaignType extends Omit<BaseCampaignType, 'startDate' | 'endDate'> {
  startDate: Date;
  endDate: Date;
}

interface DashboardProps {
  campaigns: BaseCampaignType[];
  isLoadingCampaigns: boolean;
  error: string | null;
  selectedDate: Date | null;
  selectedCampaign: BaseCampaignType | null;
  showForm: boolean;
  currentSelectedTimezone: string;
  onDateSelect: (date: Date) => void;
  onEditCampaign: (campaign: BaseCampaignType) => void;
  onDeleteCampaign: (campaignId: string) => void;
  onTimezoneUpdate: (timezone: string) => void;
  onFormClose: () => void;
  onCampaignSubmit: (campaign: Omit<BaseCampaignType, 'id'>) => void;
  onLogout: () => void;
}

const Dashboard: React.FC<DashboardProps> = ({
  campaigns,
  isLoadingCampaigns,
  error,
  selectedDate,
  selectedCampaign,
  showForm,
  currentSelectedTimezone,
  onDateSelect,
  onEditCampaign,
  onDeleteCampaign,
  onTimezoneUpdate,
  onFormClose,
  onCampaignSubmit,
  onLogout
}) => {
  // Set the document title
  useDocumentTitle('Dashboard');
  
  // Ref for file input element
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Ref to track URL cleaning
  const urlCleanedRef = useRef(false);
  // Get location to check URL parameters
  const location = useLocation();
  
  // Clean URL if it contains token parameters
  useEffect(() => {
    // Only clean once per component mount
    if (urlCleanedRef.current) return;
    
    try {
      // Check if URL has token parameter
      const url = new URL(window.location.href);
      if (url.searchParams.has('token')) {
        logAuthEvent('DASHBOARD_URL_CLEANUP', {
          message: 'Removing token from Dashboard URL',
          originalUrl: window.location.href
        });
        
        // Keep track that we've cleaned the URL
        urlCleanedRef.current = true;
        
        // Delete the token but preserve other parameters (like debug=auth)
        url.searchParams.delete('token');
        
        // Create new URL string without the token parameter
        const newUrl = url.pathname + (url.search ? url.search : '');
        
        // Use history API to replace current URL
        window.history.replaceState({}, document.title, newUrl);
        
        console.log('[DASHBOARD] Cleaned URL, removed token parameter');
      }
    } catch (err) {
      console.error('[DASHBOARD] Error cleaning URL:', err);
      logAuthEvent('DASHBOARD_URL_CLEANUP_ERROR', {
        error: err instanceof Error ? err.message : 'Unknown error',
      }, true);
    }
  }, [location]);
  
  // Add/remove modal-overlay-active class on body for browsers that don't support :has
  useEffect(() => {
    if (showForm) {
      document.body.classList.add('modal-overlay-active');
    } else {
      document.body.classList.remove('modal-overlay-active');
    }
    
    return () => {
      document.body.classList.remove('modal-overlay-active');
    };
  }, [showForm]);
  
  // Export campaigns data to JSON file
  const handleExportData = () => {
    try {
      // Create a data object with campaigns and timezone
      const exportData = {
        campaigns: campaigns,
        timezone: currentSelectedTimezone,
        exportDate: new Date().toISOString(),
        version: '1.0'
      };
      
      // Convert to JSON string
      const jsonString = JSON.stringify(exportData, null, 2);
      
      // Create a blob with the data
      const blob = new Blob([jsonString], { type: 'application/json' });
      
      // Create a URL for the blob
      const url = URL.createObjectURL(blob);
      
      // Create a temporary anchor element
      const a = document.createElement('a');
      a.href = url;
      
      // Set filename with date for uniqueness
      const date = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
      a.download = `email-campaign-calendar-${date}.json`;
      
      // Trigger the download
      document.body.appendChild(a);
      a.click();
      
      // Clean up
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting calendar data:', error);
      alert('Failed to export calendar data. Please try again.');
    }
  };
  
  // Trigger file input click
  const handleImportClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  
  // Import campaigns from uploaded JSON file
  const handleImportData = (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = event.target.files?.[0];
      if (!file) return;
      
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const fileContent = e.target?.result as string;
          const importedData = JSON.parse(fileContent);
          
          // Validate the imported data
          if (!importedData.campaigns || !Array.isArray(importedData.campaigns)) {
            throw new Error('Invalid calendar data format');
          }
          
          // Convert date strings back to Date objects
          const processedCampaigns: BaseCampaignType[] = importedData.campaigns.map((campaign: any) => ({
            ...campaign,
            startDate: new Date(campaign.startDate),
            endDate: new Date(campaign.endDate)
          }));
          
          // Note: We can't directly set campaigns anymore since we removed setCampaigns
          // The user will need to reload the page to see imported campaigns
          alert('Import functionality is temporarily disabled. Please reload the page after importing.');
        } catch (error) {
          console.error('Error parsing imported data:', error);
          alert('Failed to import calendar data. The file may be corrupted or in an incorrect format.');
        }
      };
      
      reader.onerror = () => {
        alert('Error reading file. Please try again.');
      };
      
      reader.readAsText(file);
      
      // Reset file input so the same file can be selected again
      if (event.target) {
        event.target.value = '';
      }
    } catch (error) {
      console.error('Error importing calendar data:', error);
      alert('Failed to import calendar data. Please try again.');
    }
  };

  // Convert campaigns to use strict Date objects for internal use/child components
  const campaignsWithDates: InternalCampaignType[] = campaigns.map(c => ({ 
    ...c, 
    startDate: new Date(c.startDate), 
    endDate: new Date(c.endDate) 
  }));

  return (
    <div className={showForm ? 'modal-parent' : ''}>
      {/* Show error message if there's an error */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-800">{error}</p>
        </div>
      )}
      
      {isLoadingCampaigns ? (
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner />
        </div>
      ) : (
        <div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 relative">
              <Calendar 
                campaigns={campaignsWithDates}
                onDateSelect={onDateSelect}
                onCampaignClick={(campaign) => {
                  // Convert the internal campaign back to the external format for the handler
                  const externalCampaign: BaseCampaignType = {
                    ...campaign,
                    startDate: campaign.startDate,
                    endDate: campaign.endDate,
                    notes: campaign.notes || ''
                  };
                  onEditCampaign(externalCampaign);
                }}
                selectedTimezone={currentSelectedTimezone}
                onTimezoneChange={onTimezoneUpdate}
              />
              
              {/* Export/Import buttons - positioned to align with status legend */}
              <div className="absolute bottom-2 right-4 mt-8">
                <div className="flex gap-2">
                  <button
                    onClick={handleExportData}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-[#1E3B8C] rounded-md hover:bg-[#19307a] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors shadow-sm border-0 hover:border-0 focus:border-0 active:border-0 outline-none"
                  >
                    <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                      <path d="M19,9h-4V3H9v6H5l7,7L19,9z M5,18v2h14v-2H5z"/>
                    </svg>
                    Export Calendar
                  </button>
                  <button
                    onClick={handleImportClick}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-[#1E3B8C] rounded-md hover:bg-[#19307a] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors shadow-sm border-0 hover:border-0 focus:border-0 active:border-0 outline-none"
                  >
                    <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                      <path d="M5,20h14v-2H5V20z M5,10l7-7l7,7h-4v6H9v-6H5z"/>
                    </svg>
                    Import Calendar
                  </button>
                  {/* Hidden file input for import */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".json"
                    onChange={handleImportData}
                    className="hidden"
                  />
                </div>
              </div>
            </div>
            <div>
              <CampaignList 
                campaigns={campaignsWithDates}
                onEditCampaign={onEditCampaign}
                onDeleteCampaign={onDeleteCampaign}
              />
            </div>
          </div>
        </div>
      )}

      {/* Campaign Form Modal */}
      {showForm && (selectedDate || selectedCampaign) && (
        <CampaignForm
          campaign={selectedCampaign ? { ...selectedCampaign, startDate: new Date(selectedCampaign.startDate), endDate: new Date(selectedCampaign.endDate) } : undefined}
          selectedDate={selectedDate || (selectedCampaign ? new Date(selectedCampaign.startDate) : new Date())}
          onClose={onFormClose}
          onSubmit={onCampaignSubmit}
          existingCampaigns={campaignsWithDates}
          timezone={currentSelectedTimezone}
        />
      )}
    </div>
  );
};

export default Dashboard; 